import { useCallback, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'

import { BodyText } from '@/components/atoms/BodyText'
import { DialogHeader } from '@/components/atoms/DialogHeader'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { NOTION_BUTTON_TEXT_SX } from '@/shared/styles/buttonStyles'
import { NOTION_DIALOG_PAPER_SX } from '@/shared/styles/dialogStyles'
import { FONT_WEIGHT, UI_FONT_SIZE } from '@/shared/styles/typography'
import { useToast } from '@/shared/contexts/ToastContext'
import { copyToClipboard } from '@/shared/utils/clipboard'
import { usePrescriptionReadingQuery } from '@/features/education/hooks/queries/usePrescriptionReadingQuery'
import { useSubmitReadingMutation } from '@/features/education/hooks/mutations/useSubmitReadingMutation'
import { type YakurekiDetail } from '@/shared/types/yakurekiCore'
import { RADIUS } from '@/shared/styles/radii'

interface Props {
  open: boolean
  onClose: () => void
  event: YakurekiDetail
}

// section ラベル: 28 spec の「見出しが空気を作る」= 罫線でなく余白 + fg.muted ラベルで区切る。
const Label = ({ children }: { children: React.ReactNode }) => (
  <Typography
    sx={{
      fontSize: UI_FONT_SIZE.label,
      fontWeight: FONT_WEIGHT.medium,
      color: colorTokens.fg.muted,
      mb: 1,
    }}
  >
    {children}
  </Typography>
)

// 処方の読み (Kakeru Education 1st step・ティーチングモード) のフォーカス画面。
// 薬剤師本人が「この処方をどう読むか」を書いて提出すると、AI がその読みへ「合っている点・
// 見落とし・別視点」を根拠付きで返す。書いてもらうのが前提で、AI 単独の模範解答は出さない。
// 器は他の中央 sm モーダルと同じ (NOTION_DIALOG_PAPER_SX)。入口はページ下部の行。
export const PrescriptionReadingDialog = ({ open, onClose, event }: Props) => {
  const { showToast } = useToast()
  const [note, setNote] = useState('')
  // completed でも「書き直す」で入力へ戻すためのフラグ。
  const [editing, setEditing] = useState(false)

  const { status, data, errorMessage, isLoading } = usePrescriptionReadingQuery(
    event.id,
    { enabled: open }
  )
  const submitMutation = useSubmitReadingMutation()

  // 閉じるときに状態を初期化。
  const handleClose = useCallback(() => {
    setNote('')
    setEditing(false)
    onClose()
  }, [onClose])

  // 自分の読みを提出 → AI 講評の生成を起こす。
  const handleSubmit = useCallback(async () => {
    const text = note.trim()
    if (!text) return
    try {
      await submitMutation.mutateAsync({ yakurekiId: event.id, reading: text })
      setEditing(false)
    } catch {
      showToast('提出に失敗しました', 'error')
    }
  }, [note, event.id, submitMutation, showToast])

  // 既存の講評から入力へ戻す (自分の読みを引き継いで書き直す)。
  const handleRewrite = useCallback(() => {
    setNote(data?.selfReading ?? '')
    setEditing(true)
  }, [data])

  const handleCopy = useCallback(async () => {
    if (!data?.reading) return
    if (await copyToClipboard(data.reading)) {
      showToast('AIの読みをコピーしました', 'success')
    } else {
      showToast('コピーに失敗しました', 'error')
    }
  }, [data, showToast])

  const copyAction =
    status === 'completed' && !editing && data?.reading ? (
      <Tooltip title="AIの読みをコピー">
        <IconButton size="small" onClick={() => void handleCopy()}>
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : undefined

  // 入力フォームを出すのは: 未提出 / 失敗 / 「書き直す」中。
  const showInput =
    editing || status === 'awaiting_input' || status === 'failed' || !status

  const renderBody = () => {
    if (status === 'skipped') {
      return (
        <BodyText sx={{ color: colorTokens.fg.muted }}>
          処方（NSIPS）が紐付いていないため、読みを生成できません。
        </BodyText>
      )
    }
    if (status === 'processing') {
      return (
        <Stack alignItems="center" spacing={1} sx={{ py: 2 }}>
          <CircularProgress size={20} />
          <BodyText sx={{ color: colorTokens.fg.muted }}>評価中…</BodyText>
        </Stack>
      )
    }
    if (!showInput && status === 'completed' && data) {
      return (
        <Stack spacing={3}>
          {data.selfReading && (
            <Box>
              <Label>あなたの読み</Label>
              <BodyText sx={{ whiteSpace: 'pre-wrap' }}>
                {data.selfReading}
              </BodyText>
            </Box>
          )}
          <Box>
            <Label>AIの読み</Label>
            <BodyText sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
              {data.reading}
            </BodyText>
          </Box>
          {data.citations.length > 0 && (
            <Box>
              <Label>根拠</Label>
              <Stack spacing={0.5}>
                {data.citations.map((c, i) => (
                  <Box
                    key={i}
                    sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}
                  >
                    <Box
                      component="span"
                      sx={{
                        flexShrink: 0,
                        minWidth: 16,
                        textAlign: 'center',
                        px: 0.5,
                        borderRadius: `${RADIUS.sm}px`,
                        bgcolor: colorTokens.bg.muted,
                        fontSize: UI_FONT_SIZE.micro,
                        fontWeight: FONT_WEIGHT.medium,
                        color: colorTokens.fg.muted,
                      }}
                    >
                      {c.marker}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: UI_FONT_SIZE.micro,
                        color: colorTokens.fg.muted,
                      }}
                    >
                      {c.source}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="text"
              size="small"
              onClick={handleRewrite}
              sx={NOTION_BUTTON_TEXT_SX}
            >
              書き直す
            </Button>
          </Box>
        </Stack>
      )
    }
    // 入力フォーム (未提出 / 失敗 / 書き直し中 / 初期ロード)。
    return (
      <>
        {status === 'failed' && (
          <BodyText sx={{ color: colorTokens.fg.muted, mb: 1.5 }}>
            生成に失敗しました
            {errorMessage ? `: ${errorMessage}` : ''}。もう一度お試しください。
          </BodyText>
        )}
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          placeholder="医師の処方意図をどう読む？"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={isLoading && !showInput}
          sx={{ '& .MuiInputBase-input': { fontSize: UI_FONT_SIZE.bodySm } }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
          <Button
            variant="text"
            size="small"
            onClick={() => void handleSubmit()}
            disabled={!note.trim() || submitMutation.isPending}
            sx={NOTION_BUTTON_TEXT_SX}
          >
            フィードバック実施
          </Button>
        </Box>
      </>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{ '& .MuiDialog-paper': NOTION_DIALOG_PAPER_SX }}
    >
      <DialogHeader
        title="処方を読む"
        action={copyAction}
        onClose={handleClose}
      />
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ px: 3, py: 2 }}>{renderBody()}</Box>
      </DialogContent>
    </Dialog>
  )
}
